import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import {
  BanknotesIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { ThemeToggleButton } from '../components/ThemeToggleButton';
import { useTheme } from '../contexts/ThemeContext';

const Feature: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => {
  return (
    <div className="flex flex-col items-start">
      <div className="mb-4 rounded-md bg-indigo-100 p-2 dark:bg-indigo-900">
        <div className="h-6 w-6 text-indigo-600 dark:text-indigo-300">{icon}</div>
      </div>
      <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
      <p className="text-base text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  );
};

const PricingTier: React.FC<{
  name: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  highlighted?: boolean;
  buttonLink: string;
}> = ({ name, price, description, features, buttonText, highlighted = false, buttonLink }) => {
  return (
    <div
      className={`flex flex-col rounded-lg ${
        highlighted
          ? 'border-2 border-indigo-500 bg-white shadow-xl dark:bg-gray-800'
          : 'border border-gray-200 bg-white shadow dark:border-gray-800 dark:bg-gray-900'
      } p-6`}
    >
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{name}</h3>
      <div className="mt-4 flex items-baseline">
        <span className="text-4xl font-extrabold text-gray-900 dark:text-white">{price}</span>
        {price !== 'Free' && <span className="ml-1 text-xl text-gray-500">/mo</span>}
      </div>
      <p className="mt-2 text-gray-500 dark:text-gray-400">{description}</p>
      <ul className="mb-6 mt-6 flex-1 space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="ml-3 text-base text-gray-500 dark:text-gray-400">{feature}</p>
          </li>
        ))}
      </ul>
      <Link
        to={buttonLink}
        className={`mt-auto rounded-md px-4 py-2 text-base font-medium text-white ${
          highlighted ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
        } focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
      >
        {buttonText}
      </Link>
    </div>
  );
};

const Testimonial: React.FC<{
  quote: string;
  author: string;
  role: string;
  image?: string;
}> = ({ quote, author, role, image }) => {
  return (
    <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
        <span className="text-xl font-bold text-indigo-600 dark:text-indigo-300">"</span>
      </div>
      <p className="mb-4 text-gray-500 dark:text-gray-400">{quote}</p>
      <div className="flex items-center">
        <div className="h-10 w-10 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          {image ? (
            <img src={image} alt={author} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {author.charAt(0)}
              </span>
            </div>
          )}
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-900 dark:text-white">{author}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{role}</p>
        </div>
      </div>
    </div>
  );
};

export const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const features = [
    {
      icon: <BanknotesIcon />,
      title: t('landing.features.budgeting.title'),
      description: t('landing.features.budgeting.description'),
    },
    {
      icon: <UsersIcon />,
      title: t('landing.features.sharing.title'),
      description: t('landing.features.sharing.description'),
    },
    {
      icon: <ChartBarIcon />,
      title: t('landing.features.insights.title'),
      description: t('landing.features.insights.description'),
    },
    {
      icon: <CloudArrowUpIcon />,
      title: t('landing.features.sync.title'),
      description: t('landing.features.sync.description'),
    },
    {
      icon: <GlobeAltIcon />,
      title: t('landing.features.i18n.title'),
      description: t('landing.features.i18n.description'),
    },
    {
      icon: <ShieldCheckIcon />,
      title: t('landing.features.privacy.title'),
      description: t('landing.features.privacy.description'),
    },
  ];

  const pricingTiers = [
    {
      name: t('landing.pricing.free.name'),
      price: t('landing.pricing.free.price'),
      description: t('landing.pricing.free.description'),
      features: [
        t('landing.pricing.free.feature1'),
        t('landing.pricing.free.feature2'),
        t('landing.pricing.free.feature3'),
        t('landing.pricing.free.feature4'),
        t('landing.pricing.free.feature5'),
      ],
      buttonText: t('landing.pricing.free.cta'),
      buttonLink: '/budgets/current',
    },
    {
      name: t('landing.pricing.premium.name'),
      price: t('landing.pricing.premium.price'),
      description: t('landing.pricing.premium.description'),
      features: [
        t('landing.pricing.premium.feature1'),
        t('landing.pricing.premium.feature2'),
        t('landing.pricing.premium.feature3'),
        t('landing.pricing.premium.feature4'),
        t('landing.pricing.premium.feature5'),
      ],
      buttonText: t('landing.pricing.premium.cta'),
      buttonLink: '/budgets/current',
      highlighted: true,
    },
    {
      name: t('landing.pricing.family.name'),
      price: t('landing.pricing.family.price'),
      description: t('landing.pricing.family.description'),
      features: [
        t('landing.pricing.family.feature1'),
        t('landing.pricing.family.feature2'),
        t('landing.pricing.family.feature3'),
        t('landing.pricing.family.feature4'),
        t('landing.pricing.family.feature5'),
      ],
      buttonText: t('landing.pricing.family.cta'),
      buttonLink: '/budgets/current',
    },
  ];

  const testimonials = [
    {
      quote: t('landing.testimonials.quote1'),
      author: t('landing.testimonials.author1'),
      role: t('landing.testimonials.role1'),
    },
    {
      quote: t('landing.testimonials.quote2'),
      author: t('landing.testimonials.author2'),
      role: t('landing.testimonials.role2'),
    },
    {
      quote: t('landing.testimonials.quote3'),
      author: t('landing.testimonials.author3'),
      role: t('landing.testimonials.role3'),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with Language Selector and Theme Switch */}
      <header className="bg-white shadow-sm dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex-shrink-0">
              <Link
                to="/"
                className="flex items-center text-xl font-bold text-gray-800 dark:text-white"
              >
                <img src="/logo.svg" alt="FairBalance Logo" className="mr-2 h-8 w-8" />
                FairBalance
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <ThemeToggleButton />
              <LanguageSelector />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 pb-8 pt-16 dark:from-indigo-900 dark:to-purple-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="gap-8 md:flex md:items-center md:justify-between">
            <div className="md:max-w-2xl">
              <div className="flex items-center">
                <img
                  src="/logo.svg"
                  alt="FairBalance Logo"
                  className="mr-4 h-16 w-16 sm:h-20 sm:w-20 md:h-32 md:w-32"
                />
                <div className="flex h-full flex-col justify-between">
                  <h1 className="mb-2 text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
                    FairBalance
                  </h1>
                  <p className="text-lg font-semibold text-indigo-100 sm:text-xl md:text-2xl">
                    {t('landing.hero.catchphrase')}
                  </p>
                </div>
              </div>
              <p className="mt-6 max-w-3xl text-xl text-indigo-100">{t('landing.hero.subtitle')}</p>
              <div className="mt-10 flex space-x-4">
                <Link
                  to="/budgets/current"
                  className="rounded-md bg-white px-8 py-3 text-base font-medium text-indigo-600 hover:bg-gray-100 md:px-10 md:py-4 md:text-lg"
                >
                  {t('landing.hero.primaryCta')}
                </Link>
                <a
                  href="#how-it-works"
                  className="rounded-md border border-indigo-100 border-transparent px-8 py-3 text-base font-medium text-white hover:bg-indigo-700 md:px-10 md:py-4 md:text-lg"
                >
                  {t('landing.hero.secondaryCta')}
                </a>
              </div>
            </div>
            <div className="mt-8 md:mt-0">
              <div className="rounded-lgp-2 relative h-auto w-full max-w-md shadow-xl">
                <img
                  src={
                    theme === 'dark'
                      ? '/images/app-screenshot-dark.jpg'
                      : '/images/app-screenshot-light.jpg'
                  }
                  alt="App screenshot"
                  className="rounded-md"
                  // onError={(e) => {
                  //   const target = e.target as HTMLImageElement;
                  //   target.src = `https://placehold.co/600x400?text=Budget+App+Screenshot+(${theme}+mode)`;
                  // }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              {t('landing.featuresSection.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-400">
              {t('landing.featuresSection.subtitle')}
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <Feature
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div id="how-it-works" className="bg-indigo-50 py-16 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-400">
              {t('landing.howItWorks.subtitle')}
            </p>
          </div>

          <div className="mt-12">
            <div className="grid gap-8 md:grid-cols-3">
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
                  <span className="text-xl font-bold text-indigo-600 dark:text-indigo-300">1</span>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                  {t('landing.howItWorks.step1.title')}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {t('landing.howItWorks.step1.description')}
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
                  <span className="text-xl font-bold text-indigo-600 dark:text-indigo-300">2</span>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                  {t('landing.howItWorks.step2.title')}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {t('landing.howItWorks.step2.description')}
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
                  <span className="text-xl font-bold text-indigo-600 dark:text-indigo-300">3</span>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                  {t('landing.howItWorks.step3.title')}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {t('landing.howItWorks.step3.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              {t('landing.testimonials.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-400">
              {t('landing.testimonials.subtitle')}
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <Testimonial
                key={index}
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-indigo-50 py-16 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              {t('landing.pricing.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-400">
              {t('landing.pricing.subtitle')}
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {pricingTiers.map((tier, index) => (
              <PricingTier
                key={index}
                name={tier.name}
                price={tier.price}
                description={tier.description}
                features={tier.features}
                buttonText={tier.buttonText}
                highlighted={tier.highlighted}
                buttonLink={tier.name === 'Free' ? '/budgets/current' : tier.buttonLink}
              />
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              {t('landing.faq.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-400">
              {t('landing.faq.subtitle')}
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-3xl">
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('landing.faq.q1')}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {t('landing.faq.a1')}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('landing.faq.q2')}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {t('landing.faq.a2')}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('landing.faq.q3')}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {t('landing.faq.a3')}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('landing.faq.q4')}
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                  {t('landing.faq.a4')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-indigo-600 dark:bg-indigo-900">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              {t('landing.cta.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-xl text-indigo-100">
              {t('landing.cta.subtitle')}
            </p>
            <div className="mt-8">
              <Link
                to="/budgets/current"
                className="rounded-md bg-white px-8 py-3 text-base font-medium text-indigo-600 hover:bg-gray-100 md:px-10 md:py-4 md:text-lg"
              >
                {t('landing.cta.button')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
